import { ArrowRight, CheckCircle2, Sparkles, AlertTriangle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent, EVENTS } from "@/utils/track";

const STATS = [
  { label: "Citas de hoy", value: "42", tone: "navy" },
  { label: "Confirmadas", value: "35", tone: "green" },
  { label: "En riesgo", value: "4", tone: "navy-light" },
  { label: "Huecos recuperados", value: "3", tone: "green" },
  { label: "Ingresos potencialmente recuperados", value: "1.250 €", tone: "green", wide: true },
];

const FLOW_STEPS = [
  "Cita en riesgo",
  "Confirmación inteligente",
  "Respuesta ambigua detectada",
  "Escalado a recepción / Lista de espera activada",
];

const RISK_ROWS = [
  { paciente: "Marta G.", tratamiento: "Implantología · 16:30", nivel: "Alto" },
  { paciente: "Carlos R.", tratamiento: "Limpieza · 17:15", nivel: "Medio" },
  { paciente: "Lucía P.", tratamiento: "Revisión · 18:00", nivel: "Bajo" },
];

const RISK_STYLES = {
  Bajo: "bg-[#E7F4EC] text-recover",
  Medio: "bg-[#E8EEF7] text-navy-light",
  Alto: "bg-navy text-white",
};

function DashboardMockup() {
  return (
    <div
      data-testid="hero-dashboard-mockup"
      className="relative w-full overflow-hidden rounded-2xl border border-[#E4E9F2] bg-white p-4 shadow-[0_24px_60px_-24px_rgba(11,31,58,0.25)] sm:p-6"
    >
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#D8DFEA]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#D8DFEA]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#D8DFEA]" />
        </div>
        <span className="rounded-full bg-platinum px-3 py-1 text-[11px] font-medium text-steel">
          Panel DentalAi · Demostración
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {STATS.filter((s) => !s.wide).map((stat) => (
          <div
            key={stat.label}
            className="rounded-xl border border-[#EDF1F7] bg-platinum/60 p-3"
          >
            <p
              className={`font-heading text-xl font-bold sm:text-2xl ${
                stat.tone === "green" ? "text-recover" : stat.tone === "navy-light" ? "text-navy-light" : "text-navy"
              }`}
            >
              {stat.value}
            </p>
            <p className="mt-0.5 text-[11px] font-medium leading-tight text-steel">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-xl border border-[#DDEAE2] bg-[#F2F9F5] p-3">
        <p className="font-heading text-xl font-bold text-recover sm:text-2xl">1.250 €</p>
        <p className="mt-0.5 text-[11px] font-medium text-steel">
          Ingresos potencialmente recuperados
        </p>
      </div>

      <div
        data-testid="hero-estimate-banner"
        className="mt-3 flex items-start gap-2.5 rounded-xl border border-[#DDEAE2] bg-[#F2F9F5] p-3"
      >
        <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-recover" strokeWidth={2} />
        <div>
          <p className="text-xs font-semibold leading-snug text-navy">
            Clínicas similares pueden recuperar entre 8 y 15 citas al mes
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-steel">
            Estimación orientativa según el volumen de citas y el historial de cada
            clínica. No es un resultado garantizado.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-1.5 flex items-center justify-between text-[11px] font-medium">
          <span className="text-steel">Tasa de confirmación de hoy</span>
          <span className="text-navy">83%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-[#E8EDF5]">
          <div className="h-full w-[83%] rounded-full bg-navy transition-[width] duration-700" />
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-[#EDF1F7] p-3">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-wide text-steel">
          Flujo de rescate
        </p>
        <div className="flex flex-wrap items-center gap-y-2">
          {FLOW_STEPS.map((step, i) => (
            <span key={step} className="flex items-center">
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                  i === FLOW_STEPS.length - 1
                    ? "bg-[#E7F4EC] text-recover"
                    : i === 0
                      ? "bg-navy text-white"
                      : "bg-platinum text-navy-light"
                }`}
              >
                {step}
              </span>
              {i < FLOW_STEPS.length - 1 && (
                <ArrowRight className="mx-1 h-3 w-3 shrink-0 text-steel" />
              )}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-2">
        {RISK_ROWS.map((row) => (
          <div
            key={row.paciente}
            className="flex items-center justify-between rounded-lg border border-[#EDF1F7] bg-white px-3 py-2"
          >
            <div className="flex items-center gap-2.5">
              <AlertTriangle
                className={`h-3.5 w-3.5 ${row.nivel === "Bajo" ? "text-recover" : "text-navy-light"}`}
                strokeWidth={2}
              />
              <div>
                <p className="text-xs font-semibold text-navy">{row.paciente}</p>
                <p className="text-[11px] text-steel">{row.tratamiento}</p>
              </div>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${RISK_STYLES[row.nivel]}`}
            >
              Riesgo {row.nivel.toLowerCase()}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-4 text-center text-[10px] text-steel">
        Datos ficticios con fines de demostración.
      </p>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="inicio" className="relative overflow-hidden bg-platinum">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[linear-gradient(180deg,#FFFFFF_0%,rgba(245,247,250,0)_100%)]"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8 lg:pb-28 lg:pt-24">
        <div>
          <span
            data-testid="hero-tag"
            className="fade-up inline-flex items-center gap-2 rounded-full border border-[#DCE5F0] bg-white px-4 py-1.5 text-xs font-semibold text-navy-light shadow-sm"
          >
            <Sparkles className="h-3.5 w-3.5 text-recover" strokeWidth={2} />
            IA especializada en reducir los no-shows dentales
          </span>

          <h1
            data-testid="hero-title"
            className="fade-up fade-up-1 mt-6 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-[3.4rem]"
          >
            Recupera las horas de sillón que hoy pierdes por los{" "}
            <span className="relative inline-block">
              no-shows
              <span
                aria-hidden="true"
                className="absolute inset-x-0 bottom-1 -z-10 h-3 rounded-sm bg-[#DDEAE2]"
              />
            </span>
            .
          </h1>

          <p
            data-testid="hero-subtitle"
            className="fade-up fade-up-2 mt-6 max-w-xl text-base leading-relaxed text-steel sm:text-lg"
          >
            Confirma, predice y rescata tus citas automáticamente, sin añadir trabajo a
            tu recepción.
          </p>

          <div className="fade-up fade-up-3 mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              data-testid="hero-cta-primary"
              size="lg"
              className="w-full whitespace-normal rounded-lg bg-navy px-7 py-6 text-center text-base font-semibold text-white shadow-[0_10px_24px_-10px_rgba(11,31,58,0.5)] transition-all duration-200 hover:bg-navy-light hover:shadow-[0_14px_28px_-10px_rgba(11,31,58,0.55)] sm:w-auto"
              onClick={() => trackEvent(EVENTS.CTA_PRINCIPAL, { ubicacion: "hero" })}
            >
              <a href="#calculadora">
                Calcula cuánto pierdes
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              data-testid="hero-cta-secondary"
              size="lg"
              variant="outline"
              className="w-full whitespace-normal rounded-lg border-navy/20 bg-white px-7 py-6 text-center text-base font-semibold text-navy transition-all duration-200 hover:border-navy hover:bg-platinum sm:w-auto"
              onClick={() => trackEvent(EVENTS.CTA_PRINCIPAL, { ubicacion: "hero_demo" })}
            >
              <a href="#auditoria">Solicita una demostración</a>
            </Button>
          </div>

          <p className="fade-up fade-up-4 mt-6 flex items-start gap-2 text-sm text-steel">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-recover" strokeWidth={2} />
            Se conecta a tu calendario y software dental sin sustituir tu proceso clínico.
          </p>
        </div>

        <div className="fade-up fade-up-2 lg:pl-4">
          <DashboardMockup />
        </div>
      </div>
    </section>
  );
}
